import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import "./Announcement.css"; // Import the CSS file for styling

const API_URL = process.env.REACT_APP_API_URL || "";
const ANNOUNCEMENTS_ENDPOINT = `${API_URL}api/admin/announcements`;

const audienceOptions = [
  {
    value: "all",
    label: "Everyone",
    description: "All car owners and garage owners",
  },
  {
    value: "car_owners",
    label: "Car owners",
    description: "Every registered car owner",
  },
  {
    value: "garage_owners",
    label: "Garage owners",
    description: "Every registered garage owner",
  },
  {
    value: "specific",
    label: "Specific people",
    description: "Choose Car owners and Garage owners",
  },
];

const getTokenHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// Pulls every message out of whatever shape the BE returns
const extractErrorMessages = (payload, fallback = "Request failed") => {
  if (!payload) return [fallback];

  const errors = payload.errors || payload.details || payload.error?.details;
  let messages = [];

  // Array of errors: [{ message }, { msg }, "string"]
  if (Array.isArray(errors) && errors.length) {
    messages = errors.map((e) =>
      typeof e === "string" ? e : e.message || e.msg,
    );
  }
  // Object of field errors: { title: "...", body: ["..."] }
  else if (errors && typeof errors === "object") {
    messages = Object.values(errors).flat();
  } else {
    messages = [
      payload.message,
      payload.errorMessage,
      typeof payload.error === "string" ? payload.error : null,
    ];
  }

  messages = messages.filter((m) => typeof m === "string" && m);

  return messages.length ? messages : [fallback];
};

const readJson = async (response) => {
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const messages = extractErrorMessages(payload);
    const error = new Error(messages.join("\n"));
    error.messages = messages;
    throw error;
  }

  return payload;
};

const unwrapCollection = (payload, keys) => {
  const data = payload?.data || payload;

  if (Array.isArray(data)) return data;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
};

const getUserLabel = (user) =>
  user.fullName ||
  user.name ||
  user.username ||
  user.email ||
  `User #${user.id}`;

const getGarageLabel = (garage) =>
  garage.name || garage.garage_name || garage.title || `Garage #${garage.id}`;

const audienceLabel = (value) =>
  audienceOptions.find((option) => option.value === value)?.label ||
  value ||
  "-";

// --------------------
// Searchable multi-select
// --------------------

const RecipientSelect = ({
  label,
  placeholder,
  options,
  selectedIds,
  getLabel,
  search,
  setSearch,
  onToggle,
  loading,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return options;

    return options.filter((item) => {
      const searchableText = [getLabel(item), item.email, item.username]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [options, search, getLabel]);

  const selectedOptions = options.filter((item) =>
    selectedIds.some((id) => String(id) === String(item.id)),
  );

  return (
    <div className="mb-20">
      <label className="form-label fw-semibold">{label}</label>

      <div className="border radius-8 bg-white">
        {/* Search field */}
        <div
          className="d-flex align-items-center gap-2 px-12"
          style={{ minHeight: "48px", cursor: "pointer" }}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <Icon icon="mdi:magnify" className="text-secondary-light fs-20" />

          <input
            type="text"
            className="form-control border-0 shadow-none px-0"
            style={{ cursor: "pointer" }}
            placeholder={placeholder}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsOpen(true); // typing karte waqt khula rahe
            }}
          />

          <Icon
            icon={isOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
            className="fs-20 text-secondary-light"
          />
        </div>

        {/* Selected recipients */}
        {selectedOptions.length > 0 && (
          <div className="d-flex flex-wrap gap-2 px-12 pb-12">
            {selectedOptions.map((item) => (
              <span
                key={item.id}
                className="d-inline-flex align-items-center gap-2 bg-primary-50 text-primary-600 px-10 py-6 radius-4 text-sm"
              >
                <span>{getLabel(item)}</span>

                <button
                  type="button"
                  className="border-0 bg-transparent text-primary-600 p-0 d-flex"
                  onClick={() => onToggle(item.id)}
                  aria-label={`Remove ${getLabel(item)}`}
                >
                  <Icon icon="mdi:close" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Dropdown options */}
        {isOpen && (
          <div
            className="border-top"
            style={{ maxHeight: "220px", overflowY: "auto" }}
          >
            {loading ? (
              <div className="text-center py-20 text-secondary-light">
                Loading {label.toLowerCase()}...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="text-center py-20 text-secondary-light">
                {search
                  ? "No matching results found."
                  : `No ${label.toLowerCase()} available.`}
              </div>
            ) : (
              filteredOptions.map((item) => {
                const selected = selectedIds.some(
                  (id) => String(id) === String(item.id),
                );

                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`w-100 d-flex align-items-center gap-3 px-12 py-10 border-0 text-start ${
                      selected ? "bg-primary-50" : "bg-white"
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => onToggle(item.id)}
                  >
                    <span
                      className={`d-flex align-items-center justify-content-center rounded border ${
                        selected
                          ? "bg-primary-600 border-primary-600 text-white"
                          : "border-secondary-light"
                      }`}
                      style={{
                        width: "18px",
                        height: "18px",
                        flexShrink: 0,
                      }}
                    >
                      {selected && <Icon icon="mdi:check" className="fs-14" />}
                    </span>

                    <span className="flex-grow-1 min-w-0">
                      <span className="d-block fw-medium text-primary-light text-truncate">
                        {getLabel(item)}
                      </span>

                      {item.email && (
                        <small className="text-secondary-light d-block text-truncate">
                          {item.email}
                        </small>
                      )}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      <small className="text-secondary-light d-block mt-8">
        {selectedIds.length} selected
      </small>
    </div>
  );
};

// --------------------
// Announcements
// --------------------

const AnnouncementsLayer = () => {
  const [form, setForm] = useState({
    title: "",
    body: "",
    audience: "all",
    user_ids: [],
    garage_ids: [],
  });

  const [users, setUsers] = useState([]);
  const [garages, setGarages] = useState([]);

  const [userSearch, setUserSearch] = useState("");
  const [garageSearch, setGarageSearch] = useState("");

  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recipientError, setRecipientError] = useState("");

  const [recipientsLoaded, setRecipientsLoaded] = useState(false);

  const selectedAudience = useMemo(
    () => audienceOptions.find((option) => option.value === form.audience),
    [form.audience],
  );

  // --------------------
  // Fetch users and garages
  // --------------------

  const fetchRecipients = useCallback(async () => {
    try {
      setLoadingRecipients(true);
      setRecipientError("");

      const [usersResponse, garagesResponse] = await Promise.all([
        fetch(`${API_URL}api/admin/users?limit=200&role=user`, {
          headers: getTokenHeaders(),
        }),
        fetch(`${API_URL}api/admin/garages?limit=200`, {
          headers: getTokenHeaders(),
        }),
      ]);

      const [usersPayload, garagesPayload] = await Promise.all([
        readJson(usersResponse),
        readJson(garagesResponse),
      ]);

      setUsers(
        unwrapCollection(usersPayload, ["users", "customers", "items", "rows"]),
      );

      setGarages(
        unwrapCollection(garagesPayload, ["garages", "items", "rows"]),
      );

      setRecipientsLoaded(true);
    } catch (error) {
      console.error("Unable to load announcement recipients:", error);
      setRecipientError(error.message || "Could not load users and garages.");
    } finally {
      setLoadingRecipients(false);
    }
  }, []);

  useEffect(() => {
    if (form.audience === "specific" && !recipientsLoaded) {
      fetchRecipients();
    }
  }, [form.audience, recipientsLoaded, fetchRecipients]);

  // --------------------
  // Form handlers
  // --------------------

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const toggleRecipient = (field, id) => {
    const recipientId = Number(id);

    setForm((current) => {
      const selected = current[field];

      return {
        ...current,
        [field]: selected.includes(recipientId)
          ? selected.filter((item) => item !== recipientId)
          : [...selected, recipientId],
      };
    });
  };

  const resetForm = () => {
    setForm({
      title: "",
      body: "",
      audience: "all",
      user_ids: [],
      garage_ids: [],
    });

    setUserSearch("");
    setGarageSearch("");
  };

  // --------------------
  // Submit announcement
  // --------------------

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() && !form.body.trim()) {
      toast.error("Title and body are required.", {
        toastId: "title-body-required",
      });
      return;
    }
    // Validation is handled by the backend; its error message is shown in the toast.
    const payload = {
      title: form.title.trim(),
      body: form.body.trim(),
      audience: form.audience,
      ...(form.audience === "specific"
        ? {
            user_ids: form.user_ids,
            garage_ids: form.garage_ids,
          }
        : {}),
    };

    try {
      setSubmitting(true);

      const response = await fetch(ANNOUNCEMENTS_ENDPOINT, {
        method: "POST",
        headers: getTokenHeaders(),
        body: JSON.stringify(payload),
      });

      const result = await readJson(response);

      toast.success(result.message || "Announcement sent successfully.");

      resetForm();
    } catch (error) {
      const messages = error.messages?.length
        ? error.messages
        : [error.message || "Unable to send announcement."];

      messages.forEach((message) => toast.error(message, { toastId: message }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="row gy-4">
      <div className="col-12">
        <div className="card">
          <div className="card-body p-24">
            {/* Header */}
            <div className="d-flex align-items-start justify-content-between gap-3 mb-24">
              <div>
                <h6 className="mb-4">Send announcement</h6>
                <p className="text-secondary-light mb-0">
                  Send a message to your selected audience.
                </p>
              </div>

              <span className="w-44-px h-44-px rounded-circle bg-primary-100 text-primary-600 d-flex align-items-center justify-content-center">
                <Icon icon="mdi:bullhorn-outline" className="fs-22" />
              </span>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div className="mb-20">
                <label className="form-label fw-semibold">
                  Title <span className="text-danger-600">*</span>
                </label>

                <input
                  className="form-control"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Enter title"
                />
              </div>

              {/* Message */}
              <div className="mb-20">
                <label className="form-label fw-semibold">
                  Message <span className="text-danger-600">*</span>
                </label>

                <textarea
                  className="form-control"
                  name="body"
                  value={form.body}
                  onChange={handleChange}
                  placeholder="Enter message"
                  rows={5}
                />
              </div>

              {/* Audience */}
              <div className="mb-20">
                <label className="form-label fw-semibold">
                  Audience <span className="text-danger-600">*</span>
                </label>

                <div className="position-relative">
                  <select
                    className="form-select pe-5 appearance-none"
                    name="audience"
                    value={form.audience}
                    onChange={handleChange}
                  >
                    {audienceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <Icon
                    icon="mdi:chevron-down"
                    className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary-light"
                    width="18"
                    height="18"
                  />
                </div>

                <small className="text-secondary-light d-block mt-8">
                  {selectedAudience?.description}
                </small>
              </div>

              {/* Specific recipients */}
              {form.audience === "specific" && (
                <div className="p-16 bg-neutral-50 border radius-8 mb-20">
                  <div className="d-flex align-items-center gap-2 mb-16">
                    <Icon
                      icon="mdi:account-multiple-outline"
                      className="text-primary-600 fs-20"
                    />

                    <h6 className="mb-0 text-md">Choose recipients</h6>
                  </div>

                  {recipientError && (
                    <div className="text-danger mb-16 text-sm">
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <span>{recipientError}</span>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={fetchRecipients}
                          disabled={loadingRecipients}
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  )}

                  <RecipientSelect
                    label="Car owners"
                    placeholder="Search car owners..."
                    options={users}
                    selectedIds={form.user_ids}
                    getLabel={getUserLabel}
                    search={userSearch}
                    setSearch={setUserSearch}
                    onToggle={(id) => toggleRecipient("user_ids", id)}
                    loading={loadingRecipients}
                  />

                  <RecipientSelect
                    label="Garage owners"
                    placeholder="Search garage owners..."
                    options={garages}
                    selectedIds={form.garage_ids}
                    getLabel={getGarageLabel}
                    search={garageSearch}
                    setSearch={setGarageSearch}
                    onToggle={(id) => toggleRecipient("garage_ids", id)}
                    loading={loadingRecipients}
                  />

                  <div className="d-flex align-items-center flex-wrap gap-3 text-sm text-secondary-light mt-2">
                    <span className="d-flex align-items-center gap-2">
                      <Icon icon="mdi:account-outline" />
                      {form.user_ids.length} car owners selected
                    </span>

                    <span className="d-flex align-items-center gap-2">
                      <Icon icon="mdi:garage-variant" />
                      {form.garage_ids.length} garage owners selected
                    </span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={resetForm}
                  disabled={submitting}
                >
                  Clear
                </button>

                <button
                  type="submit"
                  className="btn btn-primary-600 d-inline-flex align-items-center gap-2"
                  disabled={
                    submitting ||
                    (form.audience === "specific" && loadingRecipients)
                  }
                >
                  {submitting && (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  )}

                  {submitting ? "Sending..." : "Send announcement"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementsLayer;
